import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, GitBranch, MessageSquare, Mail, Clock, GripVertical, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_TYPES = [
  { value: 'linkedin_connection', label: 'LinkedIn Connection Request', icon: GitBranch },
  { value: 'linkedin_message', label: 'LinkedIn Message', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'wait', label: 'Wait / Condition', icon: Clock },
];

const EMPTY_STEP = { step_number: 1, type: 'linkedin_connection', delay_days: 0, template: { subject: '', body: '' }, conditions: null };

export default function SequenceEditor() {
  const { isDark } = useTheme();
  const { sequenceId } = useParams();
  const navigate = useNavigate();
  const isNew = sequenceId === 'new';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
  const [saving, setSaving] = useState(false);
  const [mergeTags, setMergeTags] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMergeTags();
    if (!isNew) fetchSequence();
  }, [sequenceId]);

  const fetchSequence = async () => {
    const res = await fetch(`${BACKEND_URL}/api/prospecting/sequences/${sequenceId}`, { credentials: 'include' });
    if (res.ok) {
      const seq = await res.json();
      setName(seq.name);
      setDescription(seq.description || '');
      setSteps(seq.steps?.length ? seq.steps : [{ ...EMPTY_STEP }]);
    }
  };

  const fetchMergeTags = async () => {
    const res = await fetch(`${BACKEND_URL}/api/prospecting/outreach/merge-tags`, { credentials: 'include' });
    if (res.ok) setMergeTags(await res.json());
  };

  const addStep = () => {
    setSteps(prev => [...prev, { ...EMPTY_STEP, step_number: prev.length + 1, type: 'wait', delay_days: 3 }]);
  };

  const removeStep = (idx) => {
    setSteps(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })));
  };

  const updateStep = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateTemplate = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, template: { ...s.template, [field]: value } } : s));
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const body = { name, description, steps: steps.map(s => ({ ...s, template: s.type === 'wait' ? null : s.template, conditions: s.type === 'wait' ? { proceed_if: s.conditions?.proceed_if || 'any', fallback_step: s.conditions?.fallback_step || null } : null })) };
    const url = isNew ? `${BACKEND_URL}/api/prospecting/sequences` : `${BACKEND_URL}/api/prospecting/sequences/${sequenceId}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
    if (res.ok) navigate('/lead-finder/sequences');
    setSaving(false);
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} border`;

  return (
    <AppLayout title={isNew ? 'New Sequence' : 'Edit Sequence'}>
      <div className="space-y-6" data-testid="sequence-editor">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/lead-finder/sequences')} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><ArrowLeft className="w-5 h-5" /></button>
          <h1 className={`text-2xl font-bold flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{isNew ? 'New Sequence' : 'Edit Sequence'}</h1>
          <button onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-sequence-btn" className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50 text-sm">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Name & Description */}
        <div className={`${card} p-5 space-y-3`}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Sequence name *" className={`${input} text-base font-medium`} data-testid="seq-name" />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" className={`${input} h-16 resize-none`} />
        </div>

        {/* Steps */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Steps</h2>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
          </div>

          {steps.map((step, idx) => {
            const typeInfo = STEP_TYPES.find(t => t.value === step.type) || STEP_TYPES[0];
            const Icon = typeInfo.icon;
            return (
              <div key={idx} className={`${card} p-4`} data-testid={`step-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'} flex-shrink-0 mt-1`}>
                    <span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{idx + 1}</span>
                  </div>

                  <div className="flex-1 space-y-3">
                    <div className="flex gap-3">
                      <select value={step.type} onChange={e => updateStep(idx, 'type', e.target.value)} className={`${input} flex-1`}>
                        {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <label className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Delay:</label>
                        <input type="number" min="0" value={step.delay_days} onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)} className={`w-16 px-2 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`} />
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>days</span>
                      </div>
                    </div>

                    {step.type === 'wait' ? (
                      <select value={step.conditions?.proceed_if || 'any'} onChange={e => updateStep(idx, 'conditions', { proceed_if: e.target.value })} className={input}>
                        <option value="any">Proceed always</option>
                        <option value="connection_accepted">Only if connection accepted</option>
                        <option value="no_reply">Only if no reply</option>
                      </select>
                    ) : (
                      <div className="space-y-2">
                        {step.type === 'email' && (
                          <input value={step.template?.subject || ''} onChange={e => updateTemplate(idx, 'subject', e.target.value)} placeholder="Email subject" className={input} />
                        )}
                        <textarea value={step.template?.body || ''} onChange={e => updateTemplate(idx, 'body', e.target.value)} placeholder="Message body... Use {first_name}, {company}, etc." rows={3} className={`${input} resize-none`} />
                        <div className="flex flex-wrap gap-1">
                          {mergeTags.slice(0, 6).map(tag => (
                            <button key={tag.tag} type="button" onClick={() => updateTemplate(idx, 'body', (step.template?.body || '') + tag.tag)} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`} title={tag.description}>
                              {tag.tag}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {steps.length > 1 && (
                    <button onClick={() => removeStep(idx)} className="p-1 rounded hover:bg-red-500/20 text-red-400 flex-shrink-0 mt-1"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}

          <button onClick={addStep} data-testid="add-step-btn" className={`w-full p-3 rounded-xl border-2 border-dashed ${isDark ? 'border-white/10 text-gray-400 hover:border-white/30 hover:text-white' : 'border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600'} flex items-center justify-center gap-2 transition-colors text-sm`}>
            <Plus className="w-4 h-4" /> Add Step
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
